/**
 * ModeShell — the single structural scaffold every mode instantiates (Page
 * Templates §2; Design System §3.1). A mode (Work, Customers, Product,
 * Operations, Agents — and the nested Hiring/Finance shells) is always the same
 * four views answering, in order: *what matters → what to do → what exists →
 * how we're doing* (Dashboard · Queue · Objects · Analytics).
 *
 * The mode name is the `PageNav` section/title; the views are NO LONGER a
 * top-of-page tab strip — they are lifted into the left ModeRail (via
 * `ModeSubNavRegistrar`), where they nest under the active mode and animate
 * open. The persistent, quiet Play strip (the attention layer) sits beneath the
 * active view's content on every view. Empty views still render — a view
 * degrades to an `EmptyState`, it never disappears (consistency > completeness).
 *
 * Pure layout: the page supplies the four hrefs (the per-mode manifest), the
 * active view's content, and the scoped Play strip. No data, no client state.
 */

import * as React from "react";
import {
  PageNav,
  type PageNavCreateItem,
  type PageNavCrumb,
} from "../page-nav";
import { NavTabs, type NavTab } from "../nav-tabs";
import { ModeSubNavRegistrar, type ModeSubNavItem } from "../mode-subnav-context";
import { ShellActionsProvider, ShellTitleBoundary } from "../shell-title-context";
import { cn } from "../utils";

/** One mode view tab (usually Dashboard/Queue/Objects/Analytics). */
export interface ModeShellView {
  href: string;
  label: string;
  /** Pre-rendered icon JSX (so the lucide ref doesn't cross server→client). */
  icon: React.ReactNode;
  /** Override active-match path (e.g. `?view=board` of Objects). */
  activeHref?: string;
  /** The section's pulse area slug — the exact join key for the ModeRail's
   *  trailing health dot. Pass it for route-owning views whose route tail differs
   *  from the pulse area (Agents ▸ Workflows → `/agents/workflows`, area `fleet`);
   *  derived from the href otherwise. */
  area?: string;
  /** Optional live count/alert bubble. */
  badge?: React.ReactNode;
}

export interface ModeShellProps {
  /** The page H1 — the LEAF location label, not a "Mode · Domain" trail. On a
   *  mode root it's the mode name ("Work"); on a domain/lane/nested sub-page it's
   *  that surface's own label ("Queue", "Finance", a Lane name). The Mode → Area
   *  path lives in the top-bar breadcrumb (NavBreadcrumb), so the H1 stays a
   *  single, non-clickable title. Used as the title only when no `crumbs` is set. */
  mode: string;
  /**
   * The owner mode SLUG (work · customers · product · operations · agents) these
   * views belong to — distinct from `mode`, a display label that may read
   * "Customers · Sales". The ModeRail uses it to attribute the registered sub-nav
   * to the right mode and to ignore a stale registration mid-navigation
   * (mode-subnav-context.tsx). Always pass it from a mode page.
   */
  modeSlug?: string;
  /**
   * Suppress the PageNav title row entirely (the H1 + its action group). Used by
   * full-bleed takeovers — e.g. the Customers cold-start, which IS its own
   * centered "Set up Customers" panel, so the redundant "Customers" H1 above it
   * is just noise. The mode breadcrumb in the top bar still names the location.
   */
  hideHeader?: boolean;
  /** Breadcrumb trail rendered AS the page title (Mode › Area › View), tracking
   *  the active location/view. Supersedes `mode` for the heading when present;
   *  pass it so the title names exactly where you are. */
  crumbs?: PageNavCrumb[];
  /** The mode's root route (Dashboard). */
  modeHref: string;
  /** The mode's views — usually the canonical four. */
  views: ModeShellView[];
  /** Pre-built "+ New" create targets, passed through to PageNav. */
  createMenu?: PageNavCreateItem[];
  /** Right-of-tabs slot — e.g. a list⇄board toggle on the Objects view. */
  right?: React.ReactNode;
  /** The active view's content. */
  children: React.ReactNode;
  /**
   * The persistent, quiet Play strip (mode-scoped), rendered beneath the
   * content on every view. Omit on views that own Play directly (the Queue).
   */
  playStrip?: React.ReactNode;
  /**
   * The context-responsive Pulse header (docs/lifecycle-spine), pinned at the top
   * of every view in the mode — directly under the title, above the content. The
   * mode root passes the commanding `size="full"` hero; a sub-page (a Domain / Lane
   * / nested view) passes the low-profile `size="strip"` wall with the current
   * band emphasized. Always carries its own gutter so it aligns to the title even
   * when `gutter={false}`. Omit to render no Pulse (e.g. Pulse disabled).
   */
  pulse?: React.ReactNode;
  /**
   * Whether ModeShell applies the page gutter (px-6) to its content. Default
   * `true` for bare content (dashboards, Objects, Analytics) that renders flush
   * to the shell. Set `false` when the child is a full, self-padded page (a
   * mounted `*Page` with its own gutter), so the gutter isn't applied twice.
   */
  gutter?: boolean;
  className?: string;
}

export function ModeShell({
  mode,
  modeSlug,
  hideHeader = false,
  crumbs,
  modeHref,
  views,
  createMenu,
  right,
  children,
  playStrip,
  pulse,
  gutter = true,
  className,
}: ModeShellProps) {
  // The views are lifted into the left ModeRail (the sub-nav under the active
  // mode) — the page nav no longer carries a tab strip (`tabs={[]}`). The same
  // shape feeds the mobile fallback (ModeSubNavItem ≡ NavTab structurally).
  const viewItems: (ModeSubNavItem & NavTab)[] = views.map((v) => ({
    href: v.href,
    label: v.label,
    icon: v.icon,
    activeHref: v.activeHref,
    area: v.area,
  }));

  return (
    <>
      <ModeSubNavRegistrar items={viewItems} mode={modeSlug} />
      {hideHeader ? null : (
        // Declare the shell owns the title for ITS OWN PageNav too. With tabs
        // always `[]` (the views live in the left ModeRail) and no actions, the
        // PageNav's `shellOwnsTitle` branch returns null — removing the redundant
        // bare-H1 sticky bar, since the leaf is already named by the top-bar
        // NavBreadcrumb + the active ModeRail row. If a mode ever passes
        // createMenu/right, that same branch still renders the action row with a
        // right-aligned spacer (no duplicate H1), exactly like a mounted page's
        // deferred PageNav.
        <ShellTitleBoundary>
          <PageNav
            section={{ label: mode, href: modeHref }}
            crumbs={crumbs}
            // No `titleHref`: the page H1 is a plain, NON-clickable title (the leaf
            // location). Navigation up the mode/domain trail lives in the left
            // ModeRail and the top-bar NavBreadcrumb — the H1 itself is just a label,
            // so clicking it (to its own route) is a no-op we deliberately omit.
            tabs={[]}
            createMenu={createMenu}
            right={right}
          />
        </ShellTitleBoundary>
      )}
      {/* Mobile fallback: the ModeRail is desktop-only (`hidden md:flex`), so
          below `md` the views render as a tab strip (the only view-switcher
          there). */}
      {viewItems.length > 0 ? (
        <div className="border-b border-border/40 px-6 md:hidden">
          <NavTabs
            tabs={viewItems}
            className="gap-6 overflow-x-auto no-scrollbar"
          />
        </div>
      ) : null}
      {/* The context-responsive Pulse header — pinned at the top of every view,
          directly under the title. It owns the canonical px-6 gutter always (even
          when the child opts out via `gutter={false}`), so the wall aligns to the
          title regardless of how the content below is padded. */}
      {/* One provider spans the title bar (which renders <ShellActionsSlot/> inside
          the pulse's SectionTitleBar) AND the mounted children (whose PageHeader
          lifts its toolbar up via <ShellActionsPortal/>) — so a mounted page's own
          create/filter/view controls sit INLINE with the shell's serif title, the
          Work▸Issues hoist generalized to every mounted view. */}
      <ShellActionsProvider>
        {pulse ? <div className="px-6 pt-4">{pulse}</div> : null}
        {/* px-6 is the canonical page gutter (matches the PageNav title/tabs row),
            so bare content's card frames line up under the title rather than
            sitting 24px to its left. Skipped when the child is a self-padded full
            page (`gutter={false}`) so the gutter isn't applied twice. pt-6 is the
            deliberate title→content step (Matt, 2026-07-17): a clear breath under
            the serif title row, still tighter than the 40px between Band sections
            so the first section reads as anchored to the title. */}
        <div className={cn("flex flex-col gap-4 pt-6 pb-4", gutter && "px-6", className)}>
          {/* The shell renders the canonical leaf title (the PageNav above).
              Declare that, so a mounted standalone page's own PageHeader/PageNav
              defers its redundant heading — one title per mode/domain route, not
              two (shell-title-context.tsx). */}
          <ShellTitleBoundary>{children}</ShellTitleBoundary>
          {/* The Play strip owns its own compact frame (ModePlay) so it can render
              NOTHING when its scoped queue is empty (`hideWhenEmpty`) — an empty
              "you're clear" card stacked beneath every view was pure noise. The
              dedicated Queue view keeps its honest empty state. */}
          {playStrip}
        </div>
      </ShellActionsProvider>
    </>
  );
}
